import React from "react";

export function KpiCard({ label, value, hint, icon: Icon, accent = "from-[#007AFF] to-[#0050C8]", trend, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-3xl p-6 transition-all duration-300 hover:-translate-y-0.5"
      style={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
        {Icon && (
          <div className={`w-9 h-9 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      <div className="font-display text-3xl sm:text-4xl font-light tracking-tight text-white">{value}</div>
      {hint && <div className="text-xs text-white/45 mt-2">{hint}</div>}
      {trend && (
        <div className={`mt-3 text-xs ${trend.positive ? "text-emerald-400" : "text-rose-400"}`}>
          {trend.positive ? "↑" : "↓"} {trend.value}
        </div>
      )}
    </div>
  );
}

export function GlassCard({ children, className = "", testId, ...props }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-3xl p-6 sm:p-8 ${className}`}
      style={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, action, testId }) {
  return (
    <div className="flex items-end justify-between mb-8" data-testid={testId}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#007AFF]/80 mb-2">Equinox OS</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-white">{title}</h1>
        {subtitle && <p className="text-white/45 mt-2 text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StageBadge({ stage, label }) {
  const colors = {
    lead: "bg-slate-400/10 text-slate-300 border-slate-400/20",
    discovery: "bg-sky-400/10 text-sky-300 border-sky-400/20",
    proposal: "bg-indigo-400/10 text-indigo-300 border-indigo-400/20",
    follow_up: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    won: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
    development: "bg-blue-400/10 text-blue-300 border-blue-400/20",
    review: "bg-violet-400/10 text-violet-300 border-violet-400/20",
    deployed: "bg-cyan-400/10 text-cyan-300 border-cyan-400/20",
    closed: "bg-teal-400/10 text-teal-300 border-teal-400/20",
    lost: "bg-rose-400/10 text-rose-300 border-rose-400/20",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-wider ${colors[stage] || colors.lead}`}>
      {label || stage.replace("_", " ")}
    </span>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#007AFF] hover:bg-[#0066CC] text-white text-sm font-medium transition-all duration-300 shadow-[0_0_18px_rgba(0,122,255,0.4)] hover:shadow-[0_0_28px_rgba(0,122,255,0.65)] disabled:opacity-50 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-medium border border-white/10 transition ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
